import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LogIn, Clock, ChevronLeft, ChevronRight, Filter, 
  Search, X, Bed, Bath, Home, MapPin, Square, ArrowDownUp,
  CheckCircle, Calendar, Eye, Star, TrendingUp, Activity,
  Building, RefreshCw, Settings, Target, Users
} from 'lucide-react';
import PropertyCard from '../components/PropertyCard';

// Animation variants mejorados
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.5, 
      ease: [0.25, 0.46, 0.45, 0.94]
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

const cardAnimation = {
  hidden: { opacity: 0, y: 20, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { 
      type: "spring", 
      damping: 20,
      stiffness: 100,
      duration: 0.5
    }
  },
  hover: {
    y: -4,
    scale: 1.02,
    transition: { duration: 0.2 }
  }
};

// Componente de header profesional
const PageHeader = ({ totalVisits, onExplore, onRefresh }) => (
  <motion.div 
    className="mb-8"
    variants={fadeInUp}
    initial="hidden"
    animate="visible"
  >
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm dark:shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4 mb-4 lg:mb-0">
            <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
              <Clock className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">Mi Historial de Visitas</h1>
              <p className="text-blue-100">
                Revisa todas las propiedades que has explorado
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <motion.button
              onClick={onRefresh}
              className="flex items-center gap-2 px-4 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 transition-all duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </motion.button>
            <motion.button
              onClick={onExplore}
              className="flex items-center gap-2 px-6 py-2.5 bg-white text-blue-600 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Home className="w-4 h-4" />
              Explorar Más
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

// Componente de filtros mejorado
const FilterBar = ({ searchQuery, setSearchQuery, sortBy, setSortBy, viewMode, setViewMode }) => (
  <motion.div 
    className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-2xl border border-gray-100 dark:border-gray-700 p-6 mb-6"
    variants={fadeInUp}
    initial="hidden"
    animate="visible"
  >
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      {/* Search */}
      <div className="relative md:w-80">
        <Search className="absolute left-3 top-3 text-gray-400 dark:text-gray-500 w-4 h-4" />
        <input
          type="text"
          placeholder="Buscar por título, ubicación o características..."
          className="pl-10 pr-4 py-2.5 w-full border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      
      <div className="flex items-center gap-3">
        {/* Sort */}
        <select
          className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                   [&>option]:bg-white [&>option]:dark:bg-gray-700 [&>option]:text-gray-900 [&>option]:dark:text-gray-100"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="recent">Más recientes</option>
          <option value="oldest">Más antiguos</option>
          <option value="price_high">Precio mayor</option>
          <option value="price_low">Precio menor</option>
        </select>
        
        {/* View mode toggle */}
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-gray-50 dark:bg-gray-700">
          <button
            className={`p-2.5 transition-colors ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
            onClick={() => setViewMode('grid')}
          >
            <div className="w-4 h-4 grid grid-cols-2 gap-0.5">
              <div className="bg-current rounded-sm"></div>
              <div className="bg-current rounded-sm"></div>
              <div className="bg-current rounded-sm"></div>
              <div className="bg-current rounded-sm"></div>
            </div>
          </button>
          <button
            className={`p-2.5 transition-colors ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
            onClick={() => setViewMode('list')}
          >
            <div className="w-4 h-4 flex flex-col gap-1">
              <div className="bg-current h-0.5 rounded-full"></div>
              <div className="bg-current h-0.5 rounded-full"></div>
              <div className="bg-current h-0.5 rounded-full"></div>
            </div>
          </button>
        </div>
      </div>
    </div>
  </motion.div>
);

// Componente de loading mejorado
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
    <motion.div
      className="text-center"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        className="relative mx-auto w-16 h-16 mb-6"
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
      >
        <div className="absolute inset-0 rounded-full border-4 border-blue-100 dark:border-gray-700"></div>
        <div className="absolute inset-0 rounded-full border-4 border-t-blue-500 dark:border-t-blue-400 border-r-transparent border-b-transparent border-l-transparent"></div>
      </motion.div>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Cargando tu historial</h3>
      <p className="text-gray-600 dark:text-gray-400">Obteniendo tus propiedades visitadas...</p>
    </motion.div>
  </div>
);

// Componente de login prompt mejorado
const LoginPrompt = ({ onLogin, navigate }) => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center py-12 px-4">
    <motion.div 
      className="max-w-lg w-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-2xl p-8 text-center border border-gray-100 dark:border-gray-700">
        <motion.div
          className="bg-gradient-to-br from-blue-500 to-purple-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg"
          whileHover={{ rotate: 5, scale: 1.05 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <Clock className="w-10 h-10 text-white" />
        </motion.div>
        
        <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-gray-100">
          Accede a tu Historial
        </h2>
        
        <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
          Inicia sesión para ver todas las propiedades que has visitado, 
          tus favoritas y continuar tu búsqueda desde donde la dejaste.
        </p>
        
        <div className="space-y-4">
          <motion.button
            onClick={onLogin}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold shadow-lg hover:shadow-xl dark:shadow-xl dark:hover:shadow-2xl transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <LogIn className="w-5 h-5" />
            Iniciar Sesión
          </motion.button>
          
          <motion.button
            onClick={() => navigate('/login')}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-medium border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Users className="w-5 h-5" />
            Crear Cuenta
          </motion.button>
        </div>
      </div>
    </motion.div>
  </div>
);

// Componente de estado vacío mejorado
const EmptyState = ({ onExplore }) => (
  <motion.div 
    className="text-center py-16"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
  >
    <motion.div 
      className="bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 w-24 h-24 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-lg dark:shadow-2xl"
      whileHover={{ rotate: 12, scale: 1.05 }}
      transition={{ type: "spring", stiffness: 300, damping: 10 }}
    >
      <Clock className="w-12 h-12 text-blue-600 dark:text-blue-400" />
    </motion.div>
    <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
      Tu historial está vacío
    </h3>
    <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-8 leading-relaxed">
      Comienza a explorar nuestro catálogo de propiedades para construir 
      tu historial personalizado de visitas.
    </p>
    <div className="space-y-3">
      <motion.button
        onClick={onExplore}
        className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl dark:shadow-xl dark:hover:shadow-2xl transition-all duration-300"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Home className="w-5 h-5" />
        Explorar Propiedades
      </motion.button>
      <div className="flex items-center justify-center gap-6 text-sm text-gray-500 dark:text-gray-400 mt-6">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4" />
          <span>Trackea tus visitas</span>
        </div>
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4" />
          <span>Guarda favoritas</span>
        </div>
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4" />
          <span>Analiza tendencias</span>
        </div>
      </div>
    </div>
  </motion.div>
);

// Componente de tarjeta de historial mejorado
const HistoricalPropertyCard = ({ property, onPropertyClick, viewMode }) => {
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };

  const formatPrice = (property) => {
    if (property.operacion === 'alquiler') {
      return `S/ ${new Intl.NumberFormat('es-PE').format(property.precio_mensual || property.precio || 0)}/mes`;
    }
    return `S/ ${new Intl.NumberFormat('es-PE').format(property.precio_soles || property.precio || 0)}`;
  };

  // Función para obtener la URL completa de la imagen
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // Si ya es una URL completa, la devolvemos tal como está
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // Si es una ruta relativa, construimos la URL completa
    const baseUrl = process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app';
    return imagePath.startsWith('/') ? `${baseUrl}${imagePath}` : `${baseUrl}/${imagePath}`;
  };
  
  if (viewMode === 'list') {
    return (
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 transition-all duration-200 overflow-hidden cursor-pointer shadow-sm dark:shadow-lg"
        onClick={() => onPropertyClick(property.id, property.operacion)}
        whileHover={{ y: -2 }}
      >
        <div className="flex">
          <div className="w-48 h-32 flex-shrink-0 relative overflow-hidden">
            {property.imagenes && property.imagenes.length > 0 && property.imagenes[0] ? (
              <img 
                src={getImageUrl(property.imagenes[0])} 
                alt={property.titulo || 'Propiedad'} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center"
              style={{ display: property.imagenes && property.imagenes.length > 0 && property.imagenes[0] ? 'none' : 'flex' }}
            >
              <Building className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <div className="absolute top-2 left-2">
              <div className={`px-2 py-1 text-xs font-medium rounded-lg text-white ${
                property.operacion === 'alquiler' ? 'bg-purple-500' : 'bg-blue-500'
              }`}>
                {property.operacion === 'alquiler' ? 'Alquiler' : 'Venta'}
              </div>
            </div>
          </div>
          
          <div className="flex-1 p-4">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg line-clamp-1">{property.titulo}</h3>
              <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatPrice(property)}</div>
            </div>
            
            <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400 mb-3">
              <MapPin className="w-4 h-4" />
              <span className="text-sm">{property.ubicacion}</span>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
              <div className="flex items-center gap-1">
                <Bed className="w-4 h-4" />
                <span>{property.habitaciones || 0}</span>
              </div>
              <div className="flex items-center gap-1">
                <Bath className="w-4 h-4" />
                <span>{property.banos || 0}</span>
              </div>
              <div className="flex items-center gap-1">
                <Square className="w-4 h-4" />
                <span>{property.area_total || 0}m²</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 text-sm font-medium">
                <Eye className="w-4 h-4" />
                <span>Visitado el {formatDate(property.fecha_visita)}</span>
              </div>
              <motion.button
                className="px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Ver detalles
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="relative">
      <motion.div
        className="transform transition-all duration-200"
        whileHover={{ y: -4 }}
      >
        <PropertyCard 
          property={property}
          disableNavigation={true}
          onFavoriteUpdate={() => {}}
        />
      </motion.div>
      
      {/* Overlay mejorado */}
      <motion.div 
        className="absolute bottom-4 left-4 right-4 z-30 cursor-pointer"
        onClick={() => onPropertyClick(property.id, property.operacion)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm px-4 py-2 rounded-lg flex items-center justify-between shadow-lg backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span className="font-medium">Visitado el {formatDate(property.fecha_visita)}</span>
          </div>
          <motion.div
            className="text-xs bg-white/20 px-2 py-1 rounded"
            whileHover={{ bg: "white/30" }}
          >
            Ver detalles
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

// Componente de paginación mejorado
const PaginationControls = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  return (
    <motion.div 
      className="flex justify-center items-center gap-2 mt-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.5 }}
    >
      <motion.button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
          currentPage === 1 
            ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 shadow-sm dark:shadow-lg'
        }`}
        whileHover={currentPage !== 1 ? { scale: 1.05 } : {}}
        whileTap={currentPage !== 1 ? { scale: 0.95 } : {}}
      >
        <ChevronLeft className="w-4 h-4" />
        Anterior
      </motion.button>

      <div className="flex items-center gap-1">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <motion.button
            key={page}
            onClick={() => onPageChange(page)}
            className={`w-10 h-10 rounded-lg font-medium transition-all duration-200 ${
              page === currentPage
                ? 'bg-blue-500 text-white shadow-lg'
                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {page}
          </motion.button>
        ))}
      </div>

      <motion.button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
          currentPage === totalPages 
            ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 shadow-sm dark:shadow-lg'
        }`}
        whileHover={currentPage !== totalPages ? { scale: 1.05 } : {}}
        whileTap={currentPage !== totalPages ? { scale: 0.95 } : {}}
      >
        Siguiente
        <ChevronRight className="w-4 h-4" />
      </motion.button>
    </motion.div>
  );
};

// Componente principal
const Historial = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [viewMode, setViewMode] = useState('grid');
  const itemsPerPage = viewMode === 'list' ? 6 : 9;

  // Verificar autenticación
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      setIsAuthenticated(!!token);
      setCheckingAuth(false);
    };
    checkAuth();
  }, []);

  // Cargar historial
  useEffect(() => {
    const fetchHistorial = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No hay token disponible');
  
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app'}/api/historial-visitas`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
  
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
  
        const data = await response.json();
        
        const enhancedData = data.map(item => {
          const isPropertyAvailable = (
            item.titulo && 
            item.titulo !== "Propiedad sin título" && 
            item.ubicacion && 
            item.ubicacion !== "Ubicación no especificada"
          );
          
          return {
            ...item,
            isAvailable: isPropertyAvailable,
            fecha_publicacion: item.fecha_visita
          };
        });
        
        setHistorial(enhancedData);
      } catch (error) {
        console.error('Error fetching visit history:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
  
    if (isAuthenticated) {
      fetchHistorial();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const handlePropertyClick = (propertyId, operacion) => {
    const route = operacion === 'alquiler' ? 'alquiler' : 'propiedad';
    navigate(`/${route}/${propertyId}`);
  };

  const handleRefresh = () => {
    setLoading(true);
    window.location.reload();
  };

  // Filtrar y ordenar
  const getFilteredAndSortedHistorial = () => {
    let filtered = historial.filter(property => property.isAvailable);
    
    // Filtrar por búsqueda
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(property => 
        property.titulo?.toLowerCase().includes(query) || 
        property.ubicacion?.toLowerCase().includes(query)
      );
    }
    
    // Ordenar
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          return new Date(b.fecha_visita) - new Date(a.fecha_visita);
        case 'oldest':
          return new Date(a.fecha_visita) - new Date(b.fecha_visita);
        case 'price_high':
          const priceA = a.operacion === 'alquiler' ? (a.precio_mensual || 0) : (a.precio_soles || 0);
          const priceB = b.operacion === 'alquiler' ? (b.precio_mensual || 0) : (b.precio_soles || 0);
          return priceB - priceA;
        case 'price_low':
          const priceA2 = a.operacion === 'alquiler' ? (a.precio_mensual || 0) : (a.precio_soles || 0);
          const priceB2 = b.operacion === 'alquiler' ? (b.precio_mensual || 0) : (b.precio_soles || 0);
          return priceA2 - priceB2;
        default:
          return 0;
      }
    });
    
    return filtered;
  };

  const filteredHistorial = getFilteredAndSortedHistorial();
  const totalPages = Math.ceil(filteredHistorial.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentHistorial = filteredHistorial.slice(startIndex, startIndex + itemsPerPage);

  // Estados condicionales
  if (checkingAuth || loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center py-12 px-4">
        <motion.div 
          className="max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-2xl p-8 text-center border border-gray-100 dark:border-gray-700"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-red-50 dark:bg-red-900/30 w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center">
            <X className="w-8 h-8 text-red-500 dark:text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Error al cargar</h2>
          <p className="text-red-600 dark:text-red-400 mb-6">{error}</p>
          <motion.button
            onClick={handleRefresh}
            className="px-6 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Reintentar
          </motion.button>
        </motion.div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPrompt onLogin={() => navigate('/login')} navigate={navigate} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <PageHeader 
          totalVisits={filteredHistorial.length}
          onExplore={() => navigate('/propiedades')}
          onRefresh={handleRefresh}
        />

        {/* Filtros */}
        <FilterBar 
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          sortBy={sortBy}
          setSortBy={setSortBy}
          viewMode={viewMode}
          setViewMode={setViewMode}
        />

        {/* Contenido principal */}
        <motion.div 
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm dark:shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="p-6">
            {historial.length === 0 ? (
              <EmptyState onExplore={() => navigate('/propiedades')} />
            ) : filteredHistorial.length === 0 ? (
              <motion.div 
                className="text-center py-16"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="bg-gray-50 dark:bg-gray-700 w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                  <Search className="w-10 h-10 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  No se encontraron resultados
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  No hay propiedades que coincidan con "{searchQuery}"
                </p>
                <motion.button
                  onClick={() => setSearchQuery('')}
                  className="px-6 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Limpiar filtros
                </motion.button>
              </motion.div>
            ) : (
              <>
                {/* Resultados info */}
                <motion.div 
                  className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100 dark:border-gray-700"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-50 dark:bg-blue-900/30 p-2 rounded-lg">
                      <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                        {filteredHistorial.length} {filteredHistorial.length === 1 ? 'propiedad encontrada' : 'propiedades encontradas'}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {searchQuery ? `Resultados para "${searchQuery}"` : 'Todas tus visitas recientes'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Página {currentPage} de {totalPages}
                  </div>
                </motion.div>

                {/* Grid/List de propiedades */}
                <AnimatePresence mode="wait">
                  <motion.div 
                    key={`${viewMode}-${currentPage}`}
                    className={
                      viewMode === 'grid' 
                        ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                        : "space-y-4"
                    }
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    {currentHistorial.map((property, index) => (
                      <motion.div
                        key={`${property.id}-${property.fecha_visita}-${index}`}
                        variants={cardAnimation}
                        layout
                      >
                        <HistoricalPropertyCard
                          property={property}
                          onPropertyClick={handlePropertyClick}
                          viewMode={viewMode}
                        />
                      </motion.div>
                    ))}
                  </motion.div>
                </AnimatePresence>

                {/* Paginación */}
                <PaginationControls 
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </>
            )}
          </div>
        </motion.div>

        {/* Footer con estadísticas */}
        {filteredHistorial.length > 0 && (
          <motion.div 
            className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-6 border border-blue-100 dark:border-blue-800"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-blue-500 p-2 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Tu Actividad de Búsqueda</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Historial actualizado automáticamente • Datos sincronizados en tiempo real
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-700 dark:text-gray-300">Propiedades en Venta</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  <span className="text-gray-700 dark:text-gray-300">Propiedades en Alquiler</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-700 dark:text-gray-300">Favoritas</span>
                </div>
              </div>
            </div>
            
            {/* Estadísticas adicionales */}
            <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {filteredHistorial.length > 0 ? 
                      Math.round((filteredHistorial.filter(p => p.operacion === 'venta').length / filteredHistorial.length) * 100) : 0}%
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Interés en Venta</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                    {filteredHistorial.length > 0 ? 
                      Math.round((filteredHistorial.filter(p => p.operacion === 'alquiler').length / filteredHistorial.length) * 100) : 0}%
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Interés en Alquiler</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-green-600 dark:text-green-400">
                    {new Set(filteredHistorial.map(p => p.ubicacion?.split(',')[0]).filter(Boolean)).size || 0}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Zonas Exploradas</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-amber-600 dark:text-amber-400">
                    {(() => {
                      if (filteredHistorial.length === 0) return '0k';
                      
                      // Calcular precio promedio manejando diferentes tipos de precios
                      const precios = filteredHistorial.map(p => {
                        let precio = 0;
                        
                        if (p.operacion === 'alquiler') {
                          precio = p.precio_mensual || p.precio || 0;
                        } else {
                          precio = p.precio_soles || p.precio || 0;
                        }
                        
                        // Asegurar que sea un número válido
                        return isNaN(precio) ? 0 : Number(precio);
                      }).filter(precio => precio > 0); // Filtrar precios válidos
                      
                      if (precios.length === 0) return '0k';
                      
                      const promedio = precios.reduce((sum, precio) => sum + precio, 0) / precios.length;
                      return Math.round(promedio / 1000) + 'k';
                    })()}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Precio Promedio</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Call to action flotante */}
        {historial.length > 0 && (
          <motion.div
            className="fixed bottom-6 right-6 z-50"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
          >
            <motion.button
              onClick={() => navigate('/propiedades')}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl shadow-lg backdrop-blur-sm font-medium"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <Home className="w-5 h-5" />
              <span>Continuar Explorando</span>
            </motion.button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Historial;